import axiosInstance from '@/lib/axios';


export const liveChatService = {
  // Get all conversations for a chatbot
  getConversations: async (
    chatBotId: string,
    status?: string,
    type?: number, // 1 for web chat, 2 for WhatsApp
    labelIds?: string,
    limit = 50,
    offset = 0
  ) => {
    const params = new URLSearchParams();
    if (status && status !== 'all') params.append('status', status);
    if (type !== undefined) params.append('type', type.toString());
    if (labelIds) params.append('labelIds', labelIds);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());

    const response = await axiosInstance.get(
      `live-chat/chatbot/${chatBotId}/conversations?${params}`
    );
    return response.data;
  },

  // Get single conversation
  getConversation: async (conversationId: string) => {
    const response = await axiosInstance.get(
      `live-chat/conversations/${conversationId}`
    );
    return response.data;
  },

  getWhatsAppConversationContact: async (conversationId: string) => {
    const response = await axiosInstance.get(
      `live-chat/conversations/${conversationId}/whatsapp-contact`
    );
    return response.data;
  },

  // Get messages for a conversation
  getMessages: async (conversationId: string, limit = 50, offset = 0) => {
    const response = await axiosInstance.get(
      `live-chat/conversations/${conversationId}/messages`,
      { params: { limit, offset } }
    );
    return response.data;
  },

  // Clear conversation messages
  clearChat: async (conversationId: string) => {
    const response = await axiosInstance.delete(
      `live-chat/conversations/${conversationId}/messages`
    );
    return response.data;
  },


  // Get customer journey timeline for a conversation
  getConversationJourney: async (
    conversationId: string,
    limit = 25,
    offset = 0
  ) => {
    const response = await axiosInstance.get(
      `live-chat/conversations/${conversationId}/journey`,
      { params: { limit, offset } }
    );
    return response.data;
  },

  // Send message as agent
  sendMessage: async (
    conversationId: string,
    content: string,
    messageType: 'text' | 'image' | 'video' | 'document' = 'text',
    mediaUrl?: string,
    filename?: string,
    replyToMessageId?: string
  ) => {
    const response = await axiosInstance.post(
      `live-chat/conversations/${conversationId}/messages`,
      { content, messageType, mediaUrl, filename, replyToMessageId }
    );
    return response.data;
  },

  // Send media message with file upload
  sendMediaMessage: async (
    conversationId: string,
    caption: string,
    file: File,
    replyToMessageId?: string,
    chatBotId?: string
  ) => {
    // First, upload the file to get a URL
    const formData = new FormData();
    formData.append('file', file);
    
    // Resolve chatbot context (default to livechat purpose for live-chat attachments)
    const resolvedChatBotId = chatBotId || localStorage.getItem('chatbotId') || '';
    if (resolvedChatBotId) {
      formData.append('chatBotId', resolvedChatBotId);
      formData.append('purpose', 'livechat');
      if (conversationId) {
        formData.append('conversationId', conversationId);
      }
    }
    
    const uploadResponse = await axiosInstance.post(
      'media/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    
    const mediaUrl = uploadResponse.data.data.media.url;
    const filename = uploadResponse.data.data.media.originalName;
    
    // Determine message type from file type
    let messageType: 'text' | 'image' | 'video' | 'audio' | 'document' = 'document';
    if (file.type.startsWith('image/')) messageType = 'image';
    else if (file.type.startsWith('video/')) messageType = 'video';
    else if (file.type.startsWith('audio/')) messageType = 'audio';
    
    // Send the message with media URL and filename
    const response = await axiosInstance.post(
      `live-chat/conversations/${conversationId}/messages`,
      { content: caption, messageType, mediaUrl, filename, replyToMessageId }
    );
    return response.data;
  },

  // Assign (or unassign) an agent to a conversation
  // Pass agentId = null to remove the current assignment
  assignAgent: async (conversationId: string, agentId: string | null) => {
    const response = await axiosInstance.patch(`live-chat/conversations/${conversationId}/assign`, {
      agentId,
    });
    return response.data;
  },

  // Update conversation status
  updateStatus: async (
    conversationId: string,
    status: 'bot_active' | 'pending_agent' | 'agent_active'
  ) => {
    const response = await axiosInstance.patch(
      `live-chat/conversations/${conversationId}/status`,
      { status }
    );
    return response.data;
  },

  // Resume bot automation
  resumeBot: async (conversationId: string) => {
    const response = await axiosInstance.post(
      `live-chat/conversations/${conversationId}/resume-bot`
    );
    return response.data;
  },

  // Mark conversation as read
  markAsRead: async (conversationId: string) => {
    const response = await axiosInstance.patch(
      `live-chat/conversations/${conversationId}/read`
    );
    return response.data;
  },

  // Request agent assistance
  requestAgent: async (conversationId: string) => {
    const response = await axiosInstance.post(
      `live-chat/conversations/${conversationId}/request-agent`
    );
    return response.data;
  },

  updateWhatsAppContactOptIn: async (contactId: string, status: boolean) => {
    const response = await axiosInstance.patch(
      `wp-crm/contacts/opt-in/${contactId}`,
      { status, source: 'manual' }
    );
    return response.data;
  },

  // Get all conversations across all user chatbots (global view)
  getGlobalConversations: async (
    chatBotId?: string,
    status?: string,
    type?: number,
    page = 1,
    perPage = 30,
    search?: string,
    agentId?: string,
    readState?: 'all' | 'read' | 'unread',
    labelIds?: string,
    source?: string
  ) => {
    const params = new URLSearchParams();
    if (chatBotId) params.append('chatBotId', chatBotId);
    if (status && status !== 'all') params.append('status', status);
    if (type !== undefined) params.append('type', type.toString());
    params.append('page', page.toString());
    params.append('perPage', perPage.toString());
    if (search) params.append('search', search);
    if (agentId) params.append('agentId', agentId);
    if (readState && readState !== 'all') params.append('readState', readState);
    if (labelIds) params.append('labelIds', labelIds);
    if (source) params.append('source', source);

    const response = await axiosInstance.get(`live-chat/global/conversations?${params}`);
    return response.data;
  },

  // Start web chat conversation
  startWebChat: async (chatBotId: string, visitorId: string) => {
    const response = await axiosInstance.post(
      `live-chat/chatbot/${chatBotId}/web-chat/start`,
      { visitorId }
    );
    return response.data;
  },

  // Start WhatsApp conversation with template
  // Step 1: Send template message using existing /wp-crm/templates/:templateId/send endpoint
  // Step 2: Get or create conversation
  startWhatsAppConversation: async (
    phoneNumber: string,
    templateId: string,
    templateVariables?: Record<string, string>,
    mediaId?: string,
    mediaFileName?: string
  ) => {
    // Format phone number (remove + if present)
    const formattedPhone = phoneNumber.replace(/^\+/, '');

    // Step 1: Send template message using existing endpoint
    const templateResponse = await axiosInstance.post(
      `wp-crm/templates/${templateId}/send`,
      {
        recipients: [{ phone: formattedPhone, variables: templateVariables }],
        mediaId,
        mediaFileName
      }
    );

    return {
      templateResult: templateResponse.data,
    };
  },

  // Get list of agents assignable to conversations for a chatbot
  getAssignableAgents: async (chatBotId: string, includeStatus = false) => {
    const params = new URLSearchParams();
    if (includeStatus) {
      params.append('includeStatus', 'true');
    }

    const query = params.toString();
    const response = await axiosInstance.get(
      `live-chat/chatbot/${chatBotId}/agents${query ? `?${query}` : ''}`
    );
    return response.data;
  },

  getConversationLabels: async (chatBotId: string) => {
    const response = await axiosInstance.get(`live-chat/chatbot/${chatBotId}/labels`);
    return response.data;
  },

  createConversationLabel: async (chatBotId: string, name: string, color?: string) => {
    const response = await axiosInstance.post(`live-chat/chatbot/${chatBotId}/labels`, { name, color });
    return response.data;
  },

  deleteConversationLabel: async (labelId: string) => {
    const response = await axiosInstance.delete(`live-chat/labels/${labelId}`);
    return response.data;
  },

  updateConversationLabels: async (conversationId: string, labelIds: string[]) => {
    const response = await axiosInstance.patch(`live-chat/conversations/${conversationId}/labels`, {
      labelIds,
    });
    return response.data;
  },

  // Toggle emoji reaction on a message
  reactToMessage: async (messageId: string, emoji: string) => {
    const response = await axiosInstance.post(
      `live-chat/messages/${messageId}/react`,
      { emoji }
    );
    return response.data;
  },
};
